import { useState } from 'react';
import { useAuditLog } from '@/hooks/useLocalStorage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar, Filter, User, Clock } from 'lucide-react';

const actionColors = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  IMPORT: 'bg-purple-100 text-purple-800',
  EXPORT: 'bg-orange-100 text-orange-800'
};

const entityColors = {
  TYPE: 'bg-blue-100 text-blue-800',
  CODE: 'bg-green-100 text-green-800',
  TYPE_EDGE: 'bg-purple-100 text-purple-800'
};

export function AuditLog() {
  const [filters, setFilters] = useState({
    entityType: 'ALL',
    action: 'ALL',
    dateFrom: '',
    dateTo: ''
  });

  const processedFilters = {
    ...filters,
    entityType: filters.entityType === 'ALL' ? '' : filters.entityType,
    action: filters.action === 'ALL' ? '' : filters.action
  };

  const { data: auditEntries, isLoading } = useAuditLog(processedFilters);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatValue = (value: any) => {
    if (!value) return '—';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-xl font-semibold text-gray-900">Audit Log</h2>
        <p className="text-gray-600 mt-1 text-md md:text-base">Track all data changes and operations</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="entityType">Entity Type</Label>
              <Select value={filters.entityType} onValueChange={(value) => handleFilterChange('entityType', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All entity types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All entity types</SelectItem>
                  <SelectItem value="TYPE">Type</SelectItem>
                  <SelectItem value="CODE">Code</SelectItem>
                  <SelectItem value="TYPE_EDGE">Type Edge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="action">Action</Label>
              <Select value={filters.action} onValueChange={(value) => handleFilterChange('action', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All actions</SelectItem>
                  <SelectItem value="CREATE">Create</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                  <SelectItem value="IMPORT">Import</SelectItem>
                  <SelectItem value="EXPORT">Export</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="dateFrom">Date From</Label>
              <Input
                type="datetime-local"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="dateTo">Date To</Label>
              <Input
                type="datetime-local"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Entries */}
      <Card>
        <CardHeader>
          <CardTitle>
            Audit Entries
            {auditEntries && ` (${auditEntries.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Entity Key</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Changes</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditEntries?.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-mono">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={actionColors[entry.action]}>
                        {entry.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={entityColors[entry.entityType]}>
                        {entry.entityType}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {entry.entityKey}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{entry.user}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {entry.action === 'UPDATE' && entry.oldValue && entry.newValue ? (
                        <details className="cursor-pointer">
                          <summary className="text-sm text-blue-600 hover:text-blue-800">
                            View changes
                          </summary>
                          <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                            <div className="mb-2">
                              <strong>Old:</strong>
                              <pre className="mt-1 text-xs overflow-auto">
                                {formatValue(entry.oldValue)}
                              </pre>
                            </div>
                            <div>
                              <strong>New:</strong>
                              <pre className="mt-1 text-xs overflow-auto">
                                {formatValue(entry.newValue)}
                              </pre>
                            </div>
                          </div>
                        </details>
                      ) : entry.action === 'CREATE' && entry.newValue ? (
                        <details className="cursor-pointer">
                          <summary className="text-sm text-green-600 hover:text-green-800">
                            View created data
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto">
                            {formatValue(entry.newValue)}
                          </pre>
                        </details>
                      ) : entry.action === 'DELETE' && entry.oldValue ? (
                        <details className="cursor-pointer">
                          <summary className="text-sm text-red-600 hover:text-red-800">
                            View deleted data
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto">
                            {formatValue(entry.oldValue)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {entry.note || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {auditEntries?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-8 w-8 mx-auto mb-2" />
              <p>No audit entries found matching the current filters.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
